import {
  FileOutlined,
  MoreOutlined,
  SearchOutlined,
  UploadOutlined
} from "@ant-design/icons"
import { App, Button, Card, DatePicker, Dropdown, Flex, Select, Table, Tag, Typography, Upload } from "antd"
import type { UploadFile } from "antd/es/upload/interface"
import dayjs, { type Dayjs } from "dayjs"
import React, { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { FilePreviewModal } from "../../../../components/FilePreviewModal/FilePreviewModal.tsx"
import { useDateFormat } from "../../../../hooks/use-date-format.ts"
import { useInfiniteScroll } from "../../../../hooks/use-table-infinite-scroll.tsx"
import { downloadFile } from "../../../../utils/download-file.ts"
import { type LeadAttachment } from "../../types/lead.types.ts"
import { fileIconFor } from "../lead-attachment-icon.tsx"
import {
  type AttachmentCategory,
  LEAD_UPLOAD_ACCEPT,
  attachmentCategory,
  attachmentDate,
  attachmentName,
  attachmentSize,
  attachmentType,
  attachmentUploadedBy,
  isImageAttachment,
  leadUploadPatch,
  validateLeadUpload,
  visibleLeadAttachments
} from "../lead-attachments.ts"
import { type LeadPatchSender } from "../lead-workspace-patch.ts"
import { TextInput } from "../../../../components/Input/TextInput.tsx"
import { LeadFilesFolderRow } from "./LeadFilesFolderRow.tsx"
import {
  type LeadFolderSummary,
  leadFilesInFolder,
  summarizeLeadFolders
} from "./lead-files-folders.ts"

const { Text } = Typography
const { RangePicker } = DatePicker

/** Every attachment already sits in memory on the lead payload — there is no
 * paginated attachments endpoint to page against, so "infinite scroll" here
 * is a client-side reveal over that array, not a network load. The short
 * delay before each reveal is the only thing that is simulated; the data
 * itself, and every permission/validation rule around it, is untouched. */
const BATCH_SIZE = 20
const REVEAL_DELAY_MS = 200

type TypeFilter = "all" | AttachmentCategory
type DateFilterKey = "all" | "today" | "7d" | "30d" | "custom"

interface LeadAttachmentsCardProps {
  attachments?: LeadAttachment[] | null
  canEdit?: boolean
  saving?: boolean
  onPatch: LeadPatchSender
}

function LeadAttachmentsCard({
  attachments,
  canEdit,
  saving,
  onPatch
}: LeadAttachmentsCardProps) {
  const { t } = useTranslation()
  const { modal } = App.useApp()
  const { parseDate } = useDateFormat()
  const [preview, setPreview] = useState<LeadAttachment | null>(null)
  const [search, setSearch] = useState("")
  const [typeFilter, setTypeFilter] = useState<TypeFilter>("all")
  const [dateFilter, setDateFilter] = useState<DateFilterKey>("all")
  const [customRange, setCustomRange] = useState<[Dayjs, Dayjs] | null>(null)
  const [visibleCount, setVisibleCount] = useState(BATCH_SIZE)
  const [revealing, setRevealing] = useState(false)
  const [openFolder, setOpenFolder] = useState<LeadFolderSummary | null>(null)

  const files = visibleLeadAttachments(attachments)
  const folders = useMemo(() => summarizeLeadFolders(files), [files])
  const scopedFiles = useMemo(
    () => leadFilesInFolder(files, openFolder?.id ?? null),
    [files, openFolder]
  )

  // Each view (root vs. a folder) starts clean — a search or filter typed
  // inside a folder must not silently narrow the other view too.
  const resetFilters = () => {
    setSearch("")
    setTypeFilter("all")
    setDateFilter("all")
    setCustomRange(null)
    setVisibleCount(BATCH_SIZE)
  }

  const openFolderView = (folder: LeadFolderSummary) => {
    setOpenFolder(folder)
    resetFilters()
  }

  const goToRootView = () => {
    setOpenFolder(null)
    resetFilters()
  }

  const matchesDate = (file: LeadAttachment) => {
    if (dateFilter === "all") return true

    const uploaded = parseDate(attachmentDate(file))
    // A date the account's parser can't read (legacy rows, or format drift
    // across the independently-deployed backend) keeps the file visible —
    // excluding it would silently drop a real file from every dated view.
    if (!uploaded) return true

    if (dateFilter === "today") return uploaded.isSame(dayjs(), "day")
    if (dateFilter === "7d")
      return uploaded.isAfter(dayjs().subtract(7, "day").startOf("day"))
    if (dateFilter === "30d")
      return uploaded.isAfter(dayjs().subtract(30, "day").startOf("day"))

    if (dateFilter === "custom" && customRange) {
      return (
        !uploaded.isBefore(customRange[0].startOf("day")) &&
        !uploaded.isAfter(customRange[1].endOf("day"))
      )
    }

    return true
  }

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase()

    return scopedFiles.filter((file) => {
      if (term && !attachmentName(file).toLowerCase().includes(term))
        return false
      if (typeFilter !== "all" && attachmentCategory(file) !== typeFilter)
        return false
      return matchesDate(file)
    })
  }, [scopedFiles, search, typeFilter, dateFilter, customRange])

  const setFilter = <T,>(setter: (value: T) => void) => (value: T) => {
    setter(value)
    setVisibleCount(BATCH_SIZE)
  }

  const hasActiveFilter =
    !!search.trim() || typeFilter !== "all" || dateFilter !== "all"

  const visible = filtered.slice(0, visibleCount)
  const hasMore = visibleCount < filtered.length

  const revealMore = async () => {
    setRevealing(true)
    await new Promise((resolve) => setTimeout(resolve, REVEAL_DELAY_MS))
    setVisibleCount((count) => count + BATCH_SIZE)
    setRevealing(false)
  }

  const { onTableScroll, containerRef } = useInfiniteScroll({
    loading: revealing,
    hasMore,
    loadMore: revealMore,
    // Keys each reveal by its start offset so the hook's duplicate-request
    // guard holds; `revealing` alone lags a render behind the first call.
    loadMoreKey: String(visibleCount)
  })

  const upload = (changed: UploadFile, fileList: UploadFile[]) => {
    const patch = leadUploadPatch(changed, fileList)
    if (patch) void onPatch(patch)
  }

  const remove = (file: LeadAttachment) =>
    onPatch({ deleted_attachment_ids: [file.id] })

  const confirmDelete = (file: LeadAttachment) => {
    modal.confirm({
      title: t("leadWorkspace.cards.attachments.deleteConfirm"),
      okText: t("leadWorkspace.cards.attachments.deleteOk"),
      okButtonProps: { danger: true },
      cancelText: t("leadWorkspace.card.cancel"),
      onOk: () => remove(file)
    })
  }

  // The one Upload action in the header — the empty state points at it
  // rather than duplicating it, per the single-upload-entry-point rule.
  const uploadButton = canEdit && (
    <Upload
      multiple
      showUploadList={false}
      accept={LEAD_UPLOAD_ACCEPT}
      beforeUpload={validateLeadUpload}
      onChange={({ file, fileList }) => upload(file, fileList)}
    >
      <Button
        type={"primary"}
        icon={<UploadOutlined />}
        loading={saving}
        aria-label={t("leadWorkspace.cards.attachments.upload")}
      >
        {t("leadWorkspace.cards.attachments.upload")}
      </Button>
    </Upload>
  )

  const column = (
    key: string,
    read: (file: LeadAttachment) => string | null
  ) => ({
    key,
    title: t(`leadWorkspace.cards.attachments.${key}`),
    render: (_: unknown, file: LeadAttachment) => {
      const value = read(file)
      return value ? (
        <Text ellipsis={{ tooltip: value }}>{value}</Text>
      ) : (
        <Text type={"secondary"}>{t("leadWorkspace.card.empty")}</Text>
      )
    }
  })

  return (
    <Card
      size={"small"}
      title={t("leadWorkspace.cards.attachments.title")}
      data-testid={"workspace-card-attachments"}
      extra={uploadButton}
    >
      <Flex vertical gap={"middle"}>
        <Text type={"secondary"} className={"text-sm"}>
          {t("leadWorkspace.cards.attachments.description")}
        </Text>

        {!files.length ? (
          // A true empty state, not an empty table — no search, no filters,
          // no headers, and no second Upload action. The header's Upload
          // button (extra, above) is the only one.
          <Flex
            vertical
            align={"center"}
            justify={"center"}
            gap={4}
            className={"py-10"}
          >
            <FileOutlined className={"text-4xl text-gray-300"} />
            <Text strong>{t("leadWorkspace.cards.attachments.emptyTitle")}</Text>
            <Text type={"secondary"} className={"max-w-xs text-center"}>
              {t("leadWorkspace.cards.attachments.emptyDescription")}
            </Text>
          </Flex>
        ) : (
          <>
            {openFolder && (
              <Flex gap={4} align={"center"}>
                <Button
                  type={"link"}
                  size={"small"}
                  className={"h-auto p-0"}
                  onClick={goToRootView}
                >
                  {t("leadWorkspace.cards.attachments.title")}
                </Button>
                <Text type={"secondary"}>{"/"}</Text>
                <Text strong>{openFolder.name}</Text>
              </Flex>
            )}

            {!openFolder && folders.length > 0 && (
              <Flex vertical gap={2}>
                {folders.map((folder) => (
                  <LeadFilesFolderRow
                    key={folder.id}
                    name={folder.name}
                    countLabel={t(
                      "leadWorkspace.cards.attachments.folderFileCount",
                      { count: folder.count }
                    )}
                    ariaLabel={t(
                      "leadWorkspace.cards.attachments.folderOpenAriaLabel",
                      { name: folder.name, count: folder.count }
                    )}
                    onOpen={() => openFolderView(folder)}
                  />
                ))}
              </Flex>
            )}

            <Flex gap={"small"} wrap>
              <TextInput disableAutoCapitalize
                allowClear
                className={"max-w-xs"}
                prefix={<SearchOutlined className={"text-gray-400"} />}
                placeholder={t(
                  "leadWorkspace.cards.attachments.searchPlaceholder"
                )}
                value={search}
                onChange={(e) => setFilter(setSearch)(e.target.value)}
              />
              <Select<TypeFilter>
                className={"w-36"}
                value={typeFilter}
                onChange={setFilter(setTypeFilter)}
                options={(
                  ["all", "documents", "images", "other"] as const
                ).map((value) => ({
                  value,
                  label: t(
                    `leadWorkspace.cards.attachments.typeFilter.${value}`
                  )
                }))}
              />
              <Select<DateFilterKey>
                className={"w-40"}
                value={dateFilter}
                onChange={setFilter(setDateFilter)}
                options={(
                  ["all", "today", "7d", "30d", "custom"] as const
                ).map((value) => ({
                  value,
                  label: t(
                    `leadWorkspace.cards.attachments.dateFilter.${value}`
                  )
                }))}
              />
              {dateFilter === "custom" && (
                <RangePicker
                  value={customRange}
                  onChange={(range) =>
                    setFilter(setCustomRange)(
                      range && range[0] && range[1]
                        ? [range[0], range[1]]
                        : null
                    )
                  }
                />
              )}
            </Flex>

            {filtered.length ? (
              <div ref={containerRef}>
              <Table<LeadAttachment>
                size={"small"}
                rowKey={"id"}
                pagination={false}
                dataSource={visible}
                scroll={{ x: "max-content", y: 420 }}
                onScroll={onTableScroll}
                columns={[
                  {
                    ...column("name", attachmentName),
                    render: (_: unknown, file: LeadAttachment) => (
                      <Flex gap={8} align={"center"} className={"min-w-0"}>
                        {isImageAttachment(file) ? (
                          <img
                            src={file.file}
                            alt={""}
                            className={"size-8 shrink-0 rounded object-cover"}
                          />
                        ) : (
                          <span className={"shrink-0 text-gray-400"}>
                            {fileIconFor(file)}
                          </span>
                        )}
                        <Text
                          ellipsis={{ tooltip: attachmentName(file) }}
                          className={"cursor-pointer"}
                          onClick={() => setPreview(file)}
                        >
                          {attachmentName(file)}
                        </Text>
                      </Flex>
                    )
                  },
                  {
                    ...column("type", attachmentType),
                    render: (_: unknown, file: LeadAttachment) => {
                      const value = attachmentType(file)
                      return value ? <Tag>{value}</Tag> : null
                    }
                  },
                  column("uploadedBy", attachmentUploadedBy),
                  column("date", attachmentDate),
                  column("size", attachmentSize),
                  {
                    key: "actions",
                    title: t("leadWorkspace.cards.attachments.actions"),
                    width: 60,
                    render: (_: unknown, file: LeadAttachment) => {
                      const name = attachmentName(file)

                      return (
                        <Dropdown
                          trigger={["click"]}
                          menu={{
                            items: [
                              {
                                key: "preview",
                                label: t(
                                  "leadWorkspace.cards.attachments.menuPreview"
                                ),
                                onClick: () => setPreview(file)
                              },
                              {
                                key: "download",
                                label: t(
                                  "leadWorkspace.cards.attachments.menuDownload"
                                ),
                                onClick: () =>
                                  void downloadFile(file.file, name)
                              },
                              ...(canEdit
                                ? [
                                    {
                                      key: "delete",
                                      danger: true,
                                      label: t(
                                        "leadWorkspace.cards.attachments.menuDelete"
                                      ),
                                      onClick: () => confirmDelete(file)
                                    }
                                  ]
                                : [])
                            ]
                          }}
                        >
                          <Button
                            size={"small"}
                            type={"text"}
                            icon={<MoreOutlined />}
                            aria-label={t(
                              "leadWorkspace.cards.attachments.moreActions",
                              { name }
                            )}
                          />
                        </Dropdown>
                      )
                    }
                  }
                ]}
              />
              </div>
            ) : hasActiveFilter ? (
              <Text type={"secondary"}>
                {t("leadWorkspace.cards.attachments.emptySearch")}
              </Text>
            ) : openFolder ? (
              <Text type={"secondary"}>
                {t("leadWorkspace.cards.attachments.folderEmpty")}
              </Text>
            ) : null}
          </>
        )}
      </Flex>

      <FilePreviewModal
        visible={!!preview}
        onClose={() => setPreview(null)}
        doc={preview}
      />
    </Card>
  )
}

export { LeadAttachmentsCard }
